/**
 * Formats a shopping list item with proper spacing
 *
 * @param {Object} item - Shopping list item
 * @param {string} item.name - Item name
 * @param {string} [item.quantity] - Item quantity
 * @param {string} [item.unit] - Item unit
 * @returns {string} Formatted item string with proper spacing
 *
 * Examples:
 * - { name: "Schultrinken", quantity: "2" } → "2 Schultrinken"
 * - { name: "Wasser", quantity: "1", unit: "Kiste" } → "1 Kiste Wasser"
 * - { name: "Gurken", quantity: "2" } → "2 Gurken"
 * - { name: "Milch" } → "Milch"
 */
export function formatShoppingItem({ quantity, unit, name }) {
  // Clean up inputs - treat null, undefined, 0, and empty string as "no quantity"
  const hasQuantity = quantity !== null && quantity !== undefined && quantity !== '' && quantity !== 0;
  const cleanQuantity = hasQuantity ? quantity.toString().trim() : '';
  const cleanUnit = unit?.trim() || '';
  const cleanName = name?.trim() || '';

  // Build the formatted string
  const parts = [];

  if (cleanQuantity) {
    parts.push(cleanQuantity);
  }

  if (cleanUnit) {
    parts.push(cleanUnit);
  }

  if (cleanName) {
    parts.push(cleanName);
  }

  // Join with single space and remove any double spaces
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Formats shopping item for display in list (with note if available)
 *
 * @param {Object} item - Shopping list item
 * @param {string} item.name - Item name
 * @param {string} [item.quantity] - Item quantity
 * @param {string} [item.unit] - Item unit
 * @param {string} [item.note] - Item note
 * @returns {Object} Object with main text and note
 */
export function formatShoppingItemWithNote(item) {
  const mainText = formatShoppingItem(item);
  const note = item.note?.trim() || '';

  return {
    mainText,
    note,
    fullText: note ? `${mainText} (${note})` : mainText
  };
}
